import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { PATH } from '@constants';
import { contextNamesSelector } from '@selectors';
import { useSelector, useTenantRole } from '@hooks';
import { useAuthentication } from '@hooks/useAuthentication';
import { useOrganizationCredits } from '@hooks/useOrganizationCredits';
import { usePlatformApps } from '@hooks/usePlatformApps';
import { path } from '@utils';

import AuthErrorPage from '@pages/AuthErrorPage';
import BucketPage from '@pages/BucketPage';
import BucketsPage from '@pages/BucketsPage';
import ClusterRequiredPage from '@pages/ClusterRequiredPage';
import DisksPage from '@pages/DisksPage';
import FlowBakesPage from '@pages/FlowBakesPage';
import FlowBakeTasksPage from '@pages/FlowBakeTasksPage';
import FlowsPage from '@pages/FlowsPage';
import ImagesPage from '@pages/ImagesPage';
import JobConstructorPage from '@pages/JobConstructorPage';
import JobsPage from '@pages/JobsPage';
import LoaderPage from '@pages/LoaderPage';
import OrganizationsInvitesPage from '@pages/OrganizationsInvitesPage';
import SecretsPage from '@pages/SecretsPage';
import StoragePage from '@pages/StoragePage';
import UserAgreeTermsPage from '@pages/UserAgreeTermsPage';
import { ApplicationRedirectHandler } from '@components/ApplicationRedirectHandler';
import { ChatPushWrapper } from '@components/Chat';
import { AuthLayout } from '@components/Layouts';
import { ScrollToTop } from '@components/ScrollToTop';

import AdminSectionRoutes from './AdminSectionRoutes';
import BillingRoutes from './BillingRoutes';
import ClusterSettingsRoutes from './ClusterSettingsRoutes';
import JobRoutes from './JobRoutes';
import OrganizationSettingsRoutes from './OrganizationSettingsRoutes';
import PlatformAppManagementRoutes from './PlatformAppManagementRoutes';
import PlatformAppsRoutes from './PlatformAppsRoutes';
import ProjectSettingsRoutes from './ProjectSettingsRoutes';
import { withSuspense } from './utils';

const OnboardingPage = lazy(() => import('@pages/OnboardingPage'));
const CreateProjectPage = lazy(() => import('@pages/CreateProjectPage'));
const CreateOrganizationPage = lazy(
  () => import('@pages/CreateOrganizationPage'),
);

const AuthPage = lazy(() => import('@pages/Auth/AuthPage'));
const InviteSignupPage = lazy(() => import('@pages/Auth/InviteSignupPage'));

// TODO: need to move Layout for one place for app with auth, not for each page separately
export const ApplicationRoutes = () => {
  const { organizationName } = useSelector(contextNamesSelector);

  const {
    isAuthenticated,
    isClusterRequired,
    isOnboardingRequired,
    termsAccepted,
    authError,
    user,
  } = useAuthentication();

  useOrganizationCredits();
  usePlatformApps();

  const { isOrganizationManager } = useTenantRole();
  const isTermsFetching = termsAccepted === undefined;

  if (authError && !user) {
    return (
      <Routes>
        <Route path="*" element={<AuthErrorPage error={authError} />} />
      </Routes>
    );
  }

  if (isAuthenticated && isTermsFetching) {
    return (
      <Routes>
        <Route path="*" element={<LoaderPage />} />
      </Routes>
    );
  }

  if (isAuthenticated && !termsAccepted) {
    return (
      <Routes>
        <Route path="*" element={<UserAgreeTermsPage />} />
      </Routes>
    );
  }

  if (isOnboardingRequired) {
    return (
      <Routes>
        <Route path={PATH.ONBOARDING} element={withSuspense(OnboardingPage)} />
        <Route path="*" element={<Navigate replace to={PATH.ONBOARDING} />} />
      </Routes>
    );
  }

  if (isClusterRequired) {
    return (
      <Routes>
        <Route
          path={path.catchAll(PATH.ORGANIZATION_SETTINGS)}
          element={<OrganizationSettingsRoutes />}
        />
        <Route
          path={PATH.ORGANIZATIONS_INVITES}
          element={<OrganizationsInvitesPage />}
        />
        <Route path="*" element={<ClusterRequiredPage />} />
      </Routes>
    );
  }

  if (isAuthenticated) {
    return (
      <ChatPushWrapper>
        <ScrollToTop />
        <Routes>
          <Route
            path={path.catchAll(PATH.APPS)}
            element={<PlatformAppsRoutes />}
          />
          <Route
            path={path.catchAll(path.appsInstance())}
            element={<PlatformAppManagementRoutes />}
          />

          <Route path={path.catchAll(PATH.STORAGE)} element={<StoragePage />} />
          <Route path={PATH.BUCKETS} element={<BucketsPage />} />
          <Route path={path.catchAll(path.bucket())} element={<BucketPage />} />
          <Route path={PATH.DISKS} element={<DisksPage />} />
          <Route path={path.catchAll(PATH.IMAGES)} element={<ImagesPage />} />
          <Route path={PATH.SECRETS} element={<SecretsPage />} />

          <Route
            path={path.catchAll(path.job())}
            element={<JobRoutes isJobPage />}
          />

          {isOrganizationManager && organizationName && (
            <Route
              path={path.catchAll(PATH.BILLING)}
              element={<BillingRoutes />}
            />
          )}

          <Route path={PATH.JOBS} element={<JobsPage />} />
          <Route path={PATH.NEW_JOB} element={<JobConstructorPage />} />
          <Route path={path.bake()} element={<FlowBakeTasksPage />} />
          <Route path={path.flowBakes()} element={<FlowBakesPage />} />
          <Route path={PATH.FLOWS} element={<FlowsPage />} />

          <Route
            path={PATH.CREATE_PROJECT}
            element={withSuspense(CreateProjectPage)}
          />
          <Route
            path={PATH.ORGANIZATIONS_INVITES}
            element={<OrganizationsInvitesPage />}
          />
          <Route
            path={PATH.CREATE_ORGANIZATION}
            element={withSuspense(CreateOrganizationPage)}
          />

          <Route
            path={path.catchAll(PATH.CLUSTER_SETTINGS)}
            element={<ClusterSettingsRoutes />}
          />
          <Route
            path={path.catchAll(PATH.ORGANIZATION_SETTINGS)}
            element={<OrganizationSettingsRoutes />}
          />
          <Route
            path={path.catchAll(PATH.PROJECT_SETTINGS)}
            element={<ProjectSettingsRoutes />}
          />

          <Route
            path={path.catchAll(PATH.ADMIN_SECTION)}
            element={<AdminSectionRoutes />}
          />

          <Route path="*" element={<ApplicationRedirectHandler />} />
        </Routes>
      </ChatPushWrapper>
    );
  }

  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path={PATH.AUTH} element={withSuspense(AuthPage)} />
        <Route
          path={PATH.INVITE_SIGNUP}
          element={withSuspense(InviteSignupPage)}
        />
      </Route>
      <Route path="*" element={<Navigate replace to={PATH.AUTH} />} />
    </Routes>
  );
};
